import { useTranslation } from "react-i18next";
import { SocialSentiment } from "@digital-twins/shared-types";
import { cn } from "@/lib/utils";
import { sharesLargestRemainder } from "./charts/socialTopicMetrics";
import { SocialTopicSlotContext } from "./SocialTopicView";
import { useTopicHighlight } from "./SocialTopicHighlightContext";
import { useSocialUniverseCounts } from "./useSocialUniverse";

interface SocialSentimentBarProps {
  /** Reads the sentiment facet counts; a segment click sets the sentiment filter. */
  context: SocialTopicSlotContext;
}

/** Left to right: negative to positive, like the header scale. */
const ORDER: ReadonlyArray<SocialSentiment> = ["negative", "mixed", "neutral", "positive"];

const SEGMENT_CLASS: Record<SocialSentiment, string> = {
  negative: "bg-chart-negative",
  mixed: "bg-foreground/45",
  neutral: "bg-foreground/20",
  positive: "bg-chart-positive",
};

/**
 * The stats band's sentiment cell: the net figure and one bar split by
 * sentiment share. The bar reads the sentiment facet (every filter but
 * sentiment itself); a segment click sets the sentiment filter, the same
 * click again clears it. Without a sentiment on any post: n/a over an empty
 * track, never a made-up split.
 */
export function SocialSentimentBar(props: SocialSentimentBarProps) {
  const { context } = props;
  const { universe, topicFilters, refreshKey } = context;

  const { t } = useTranslation("app");
  const { setActive } = useTopicHighlight();

  const { counts } = useSocialUniverseCounts(
    universe.token,
    { groupBy: "sentiment", filters: topicFilters.facetFilters("sentiment") },
    refreshKey,
  );

  const activeSentiment = topicFilters.filters.sentiment;
  const bySentiment: Record<SocialSentiment, number> = {
    negative: 0,
    mixed: 0,
    neutral: 0,
    positive: 0,
  };
  for (const cell of counts?.cells ?? []) {
    if (cell.key in bySentiment) {
      bySentiment[cell.key as SocialSentiment] = cell.count;
    }
  }
  const bandCounts = ORDER.map((key) => bySentiment[key]);
  const withSentiment = bandCounts.reduce((sum, count) => sum + count, 0);
  const shares = withSentiment > 0 ? sharesLargestRemainder(bandCounts) : null;
  const net =
    withSentiment > 0
      ? Math.round(((bySentiment.positive - bySentiment.negative) * 100) / withSentiment)
      : null;

  const onToggle = (sentiment: SocialSentiment) => {
    topicFilters.setFilter("sentiment", activeSentiment === sentiment ? null : sentiment);
  };

  // No post carries a sentiment: the cell would only say n/a, so the band gives its slot away.
  if (counts && withSentiment === 0) {
    return null;
  }

  return (
    <div className="flex min-w-0 flex-col gap-0.5" data-testid="social-topic-kpi-sentiment">
      <span className="text-xs text-muted-foreground">
        {t("socialListening.topicPage.sentiment.ariaLabel")}
      </span>
      <span
        className="text-[22px] font-semibold leading-tight tracking-[-0.02em] tabular-nums text-foreground"
        data-testid="social-topic-kpi-sentiment-value"
      >
        {net == null
          ? t("socialListening.topicPage.stats.notAvailable")
          : `${net > 0 ? "+" : ""}${net}`}
      </span>
      <div
        className="mt-1 flex h-1.5 w-full min-w-[96px] max-w-[160px] gap-px overflow-hidden rounded-full bg-muted"
        data-testid="social-topic-kpi-sentiment-bar"
      >
        {shares
          ? ORDER.map((sentiment, index) =>
              shares[index] > 0 ? (
                <button
                  key={sentiment}
                  type="button"
                  aria-pressed={activeSentiment === sentiment}
                  aria-label={t(`socialListening.topicPage.sentiment.share.${sentiment}`, {
                    percent: shares[index],
                  })}
                  title={t(`socialListening.topicPage.sentiment.share.${sentiment}`, {
                    percent: shares[index],
                  })}
                  className={cn(
                    "h-full transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring",
                    SEGMENT_CLASS[sentiment],
                    activeSentiment && activeSentiment !== sentiment && "opacity-25",
                  )}
                  style={{ width: `${shares[index]}%` }}
                  data-testid={`social-topic-kpi-sentiment-${sentiment}`}
                  onClick={() => onToggle(sentiment)}
                  onMouseEnter={() => setActive({ kind: "sentiment", sentiment })}
                  onMouseLeave={() => setActive(null)}
                />
              ) : null,
            )
          : null}
      </div>
      <span
        className="min-h-[18px] text-xs text-muted-foreground tabular-nums"
        data-testid="social-topic-kpi-sentiment-note"
      >
        {t("socialListening.topicPage.sentiment.withSentiment", { count: withSentiment })}
      </span>
    </div>
  );
}
